/* Dependencies */
import anime from 'animejs';

// Models
import {
  NavigationItem,
  NavigationDrawer,
  MegaMenuDesktopProps,
  SubMenuItem,
} from './MegaMenuDesktop.model';

/**
 * Mega Menu Desktop
 * @class
 */
class MegaMenuDesktop {
  public menuElement: HTMLElement;
  public navItems: NavigationItem[] = [];
  public activeNavItem: NavigationItem;
  public activeSubMenu: SubMenuItem;

  constructor(props: MegaMenuDesktopProps) {
    this.menuElement = props.menuElement;
    this.defineElements();
    this.matchBannerColor();
  }

  /**
   * Match the menu to the banner color.
   */
  matchBannerColor(): void {
    // Check if the banner match is present
    const colorMatch: HTMLElement = document.querySelector(
      '[data-menu-match-color]'
    );

    // Locate content container
    const containerElement: HTMLElement = this.menuElement.querySelector(
      '[data-menu-container]'
    );

    // If not present, return
    if (!colorMatch) {
      if (document.body.classList.contains('tw-bg-peach')) {
        this.menuElement.classList.add('tw-bg-peach');
        containerElement.classList.add('tw-bg-peach');
      } else {
        this.menuElement.classList.add('tw-bg-white');
        containerElement.classList.add('tw-bg-white');
      }
      return;
    }

    // Apply color
    this.menuElement.style.backgroundColor = colorMatch.style.backgroundColor;
    containerElement.style.backgroundColor = colorMatch.style.backgroundColor;
  }

  /**
   * Defines the elements within the menu.
   */
  defineElements(): void {
    // Retrieve all top leel nav items
    const topLevelNavItems = Array.from(
      this.menuElement.querySelectorAll('[data-menu-item]')
    ) as HTMLElement[];

    // Handle edge case of no items
    if (!topLevelNavItems.length) return;

    // Loop through top level nav items
    topLevelNavItems.forEach((navItem) => {
      // Retrieve the drawer element
      const drawer = navItem.querySelector('[data-menu-drawer]') as HTMLElement;

      // Retrieve the sub menu items
      const subMenuNavItems: HTMLElement[] = Array.from(
        navItem.querySelectorAll('[data-sub-menu-trigger]')
      );

      // Store the nav item.
      this.navItems.push({
        parentNavElement: navItem.querySelector('[data-menu-item-trigger]'),
        drawer: drawer
          ? {
              drawerElement: drawer,
              subMenuItems: subMenuNavItems.map((subMenuNavItem) => {
                return {
                  subMenuNavElement: subMenuNavItem,
                  subMenuElement: subMenuNavItem.parentNode.querySelector(
                    '[data-sub-menu]'
                  ) as HTMLElement,
                };
              }),
            }
          : null,
      });
    });

    // Attach the event handlers.
    this.attachEvents();
  }

  /**
   * Attaches relevant event handlers for the menu.
   */
  attachEvents(): void {
    // Handle document resize.
    let resizeTimeout: NodeJS.Timeout;
    document.addEventListener('resize', (event) => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        if (this.activeNavItem) {
          this.updateDrawerHeight();
        }
      }, 50);
    });

    // Handle user clicking out of menu.
    document.addEventListener('click', (event) => {
      const target = event.target as HTMLElement;
      if (!target.closest('[data-menu-item]') && this.activeNavItem) {
        this.closeDrawer();
        return;
      }
    });

    // Handle closing menu with Escape key.
    document.addEventListener('keyup', (event) => {
      if (!this.activeNavItem) return;
      if (event.key === 'Escape') {
        // Re-focus on the top level nav.
        this.activeNavItem.parentNavElement.focus();

        // Close the drawer.
        this.closeDrawer();
      }
    });

    // Add the event handlers to the navigation items.
    this.navItems.forEach((navItem) => {
      if (!navItem.drawer) return;

      // Top level nav open drawer.
      navItem.parentNavElement.addEventListener('click', (event) => {
        event.preventDefault();
        this.openDrawer(navItem);
      });

      // Sub menu items
      if (navItem.drawer.subMenuItems) {
        navItem.drawer.subMenuItems.forEach((subMenuItem) => {
          subMenuItem.subMenuNavElement.addEventListener('click', (event) => {
            event.preventDefault();
            this.openSubMenu(subMenuItem);
          });
        });
      }
    });
  }

  /**
   * Open the drawer.
   * @param drawer - The drawer to open.
   * @returns
   */
  openDrawer(navItem: NavigationItem): void {
    // Ignore if the drawer is already open.
    if (this.activeNavItem === navItem) return;

    const drawerElement = navItem.drawer.drawerElement;
    const currentNavItem = this.activeNavItem;
    const currentDrawer = this.activeNavItem ? this.activeNavItem.drawer : null;

    // Set the new drawer.
    this.activeNavItem = navItem;
    document.body.setAttribute('data-menu-open', 'true');

    // Create animation timeline for drawer open.
    const timeline = anime.timeline({
      autoplay: false,
      loop: false,
    });

    // Animate in new drawer
    if (currentDrawer) {
      // Quickly animate in the new drawer.
      timeline.add({
        targets: drawerElement,
        opacity: [0, 1],
        easing: 'linear',
        duration: 100,
        begin: () => {
          this.closeSubMenu();
          drawerElement.style.transform = 'translateY(0)';
          drawerElement.classList.add('tw-flex');
          drawerElement.classList.remove('tw-hidden');
          drawerElement.classList.add('tw-opacity-1');
          drawerElement.classList.remove('tw-opacity-0');
        },
        complete: () => {
          // Update the aria attributes.
          this.setNavigationExpanded(this.activeNavItem, true);
        },
      });
    } else {
      // Animate in the new drawer fully.
      timeline.add({
        targets: drawerElement,
        translateY: ['-100%', 0],
        easing: 'linear',
        duration: 200,
        begin: () => {
          drawerElement.classList.add('tw-flex');
          drawerElement.classList.remove('tw-hidden');
          drawerElement.classList.add('tw-opacity-1');
          drawerElement.classList.remove('tw-opacity-0');
        },
        complete: () => {
          // Update the aria attributes.
          this.setNavigationExpanded(this.activeNavItem, true);
        },
      });
    }

    // Animate out existing drawer
    if (currentDrawer) {
      timeline.add({
        targets: currentDrawer.drawerElement,
        opacity: [1, 0],
        easing: 'linear',
        duration: 100,
        complete: () => {
          // Update the util classes on removal.
          currentDrawer.drawerElement.classList.remove('tw-flex');
          currentDrawer.drawerElement.classList.add('tw-hidden');
          currentDrawer.drawerElement.classList.remove('tw-opacity-1');
          currentDrawer.drawerElement.classList.add('tw-opacity-0');
          currentDrawer.drawerElement.style.opacity = null;

          // Update the aria attributes.
          this.setNavigationExpanded(currentNavItem, false);

          // Reset the closed drawer height.
          this.resetDrawerHeight(currentDrawer);
        },
      });
    }

    timeline.play();
  }

  /**
   * Closes the active drawer.
   */
  closeDrawer(): void {
    // Ignore if there is no active drawer.
    if (!this.activeNavItem) return;

    const drawerElement = this.activeNavItem.drawer.drawerElement;

    // Animate the drawer closed.
    anime({
      targets: drawerElement,
      translateY: [0, '-100%'],
      easing: 'linear',
      duration: 200,
      complete: () => {
        // Update util classes.
        drawerElement.classList.remove('tw-flex');
        drawerElement.classList.add('tw-hidden');
        drawerElement.classList.remove('tw-opacity-1');
        drawerElement.classList.add('tw-opacity-0');

        // Reset the drawer height and close sub menu.
        this.resetDrawerHeight(this.activeNavItem.drawer);
        this.closeSubMenu();

        // Update the aria attributes.
        this.setNavigationExpanded(this.activeNavItem, false);

        // Reset the drawer state.
        this.activeNavItem = null;
        document.body.removeAttribute('data-menu-open');
      },
    });
  }

  /**
   * Sets The main navigation expanded state.
   * @param navItem - The nav item to be updated.
   * @param expanded - The expanded state to be applied.
   */
  setNavigationExpanded(navItem: NavigationItem, expanded: boolean): void {
    navItem.parentNavElement.setAttribute('aria-expanded', expanded.toString());
  }

  /**
   * Sets the sub navigation expanded state.
   * @param subMenu - The sub menu item to be updated.
   * @param expanded - The expanded state to be applied.
   */
  setSubNavigationExpanded(subNavItem: SubMenuItem, expanded: boolean): void {
    subNavItem.subMenuNavElement.setAttribute(
      'aria-expanded',
      expanded.toString()
    );
  }

  /**
   * Calculates the new height of the active drawer in relation to the active sub menu.
   * @returns - Current and new height values.
   */
  calculateDrawerHeight(): {
    currentDrawerHeight: number;
    newDrawerHeight: number;
  } {
    return {
      currentDrawerHeight: this.activeNavItem.drawer.drawerElement.clientHeight,
      newDrawerHeight: this.activeSubMenu.subMenuElement.clientHeight + 50,
    };
  }

  /**
   * Updates the height of the active drawer.
   */
  updateDrawerHeight(): void {
    if (!this.activeSubMenu) return;

    let { currentDrawerHeight, newDrawerHeight } = this.calculateDrawerHeight();

    // Enforce minimum drawer height
    if (newDrawerHeight < 310) {
      newDrawerHeight = 310;
    }

    // Animate to the new height.
    anime({
      begin: async () => {
        this.activeSubMenu.subMenuElement.classList.add('tw-opacity-0');
        this.activeSubMenu.subMenuElement.classList.remove('tw-hidden');
      },
      targets: this.activeNavItem.drawer.drawerElement,
      minHeight: [`${currentDrawerHeight}px`, `${newDrawerHeight}px`],
      easing: 'linear',
      duration: 200,
    });
  }

  /**
   * Resets a desired drawers height.
   * @param drawer - The drawer to be reset.
   * @returns
   */
  resetDrawerHeight(drawer: NavigationDrawer): void {
    if (!drawer) return;
    drawer.drawerElement.style.minHeight = null;
  }

  /**
   * Opens the sub menu.
   * @param subMenu - The sub menu to open.
   * @returns
   */
  openSubMenu(subMenu: SubMenuItem): void {
    // Ingnore is sub menu is already open.
    if (this.activeSubMenu === subMenu) return;

    const currentMenu = this.activeSubMenu;
    const timeline = anime.timeline({
      loop: false,
      autoplay: false,
    });

    // Animate out active sub menu
    if (this.activeSubMenu) {
      timeline.add({
        targets: currentMenu.subMenuElement,
        opacity: [1, 0],
        easing: 'linear',
        duration: 50,
        complete: () => {
          currentMenu.subMenuElement.classList.add('tw-hidden');
          currentMenu.subMenuElement.classList.add('tw-opacity-0');
          currentMenu.subMenuNavElement.removeAttribute('data-active');

          // Update the aria attributes.
          this.setSubNavigationExpanded(currentMenu, false);
        },
      });
    }

    // Set the new active sub menu
    this.activeSubMenu = subMenu;

    // Prepare the menu for animation.
    subMenu.subMenuElement.classList.add('tw-opacity-0');
    subMenu.subMenuElement.classList.remove('tw-hidden');
    subMenu.subMenuNavElement.setAttribute('data-active', 'true');

    let { currentDrawerHeight, newDrawerHeight } = this.calculateDrawerHeight();

    // Enforce minimum drawer height
    if (newDrawerHeight < 310) {
      newDrawerHeight = 310;
    }

    // Animate the drawer height and render new menu
    timeline.add({
      targets: this.activeNavItem.drawer.drawerElement,
      minHeight: [`${currentDrawerHeight}px`, `${newDrawerHeight}px`],
      easing: 'linear',
      duration: 200,
    });

    // Animate in new sub menu
    timeline.add(
      {
        targets: subMenu.subMenuElement,
        opacity: [0, 1],
        easing: 'linear',
        duration: 200,
        complete: () => {
          subMenu.subMenuElement.classList.remove('tw-opacity-0');
          subMenu.subMenuElement.style.opacity = null;

          // Update the aria attributes.
          this.setSubNavigationExpanded(subMenu, true);
        },
      },
      '-=100'
    );

    timeline.play();
  }

  /**
   * Close the active sub menu.
   * @returns
   */
  closeSubMenu(): void {
    // Ensure there is an active sub menu.
    if (!this.activeSubMenu) return;

    // Update the menu styles.
    this.activeSubMenu.subMenuNavElement.removeAttribute('data-active');
    this.activeSubMenu.subMenuElement.classList.add('tw-hidden');
    this.activeSubMenu.subMenuElement.classList.add('tw-opacity-0');

    // Update the aria attributes.
    this.setSubNavigationExpanded(this.activeSubMenu, false);

    // Reset state
    this.activeSubMenu = null;
  }
}

/**
 * Initiates the mega menu instance.
 * @returns
 */
export const megaMenuDesktopInit = (): void => {
  // Retrieve the element
  const megaMenuElement = document.querySelector(
    '[data-mega-menu-desktop]'
  ) as HTMLElement;

  // Esnure element is present.
  if (!megaMenuElement) return;

  // Create a new mega menu instance
  new MegaMenuDesktop({ menuElement: megaMenuElement });
};
